import React, { useEffect, useState } from 'react';
import PromptModal from './PromptModal';
import PointDialogModal from './PointDialogModal';
import { StageBadge } from './pipelineStage';

// Compact generation strip injected into each location of a pipeline-draft quest
// (LocationsList renders it via renderPipelineControls). The location's text is
// shown by LocationsList itself; everything generative happens inside the ONE
// point dialog (PointDialogModal): the 🃏 task phase (candidate cards, the
// human's pick), then the ✍️ writer ↔ critic loop, then the formalized draft.
// The strip only shows derived state and opens that dialog.
async function papi(path, body) {
  const res = await fetch(`/api/pipeline${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  let data = null;
  try { data = await res.json(); } catch (e) {}
  if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status}`);
  return data;
}

const PipelineLocationControls = ({ questId, location, point, onChange }) => {
  const uuid = location.uuid;
  const hasDraft = !!(point && point.draft);
  const criticLoop = (point && point.critic_loop) || null;
  const [errors, setErrors] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Editorial guidance (location.author_guidance): a persistent steer that the
  // writer's brief and every critic call receive as the GUIDANCE_BLOCK fragment.
  // Saved via the generic location-field PATCH; empty = no block in the prompts.
  const [showGuidance, setShowGuidance] = useState(false);
  const [guidanceDraft, setGuidanceDraft] = useState(location.author_guidance || '');
  const [guidanceStatus, setGuidanceStatus] = useState('idle');
  useEffect(() => { setGuidanceDraft(location.author_guidance || ''); }, [uuid, location.author_guidance]);
  const guidanceSaved = (location.author_guidance || '').trim();

  const saveGuidance = async () => {
    setGuidanceStatus('saving');
    try {
      const res = await fetch(`/api/quest/${questId}/location-field`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ locationUuid: uuid, field: 'author_guidance', value: guidanceDraft.trim() }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setGuidanceStatus('saved');
      setTimeout(() => setGuidanceStatus('idle'), 1500);
      if (onChange) onChange();
    } catch (e) {
      setGuidanceStatus('error');
    }
  };

  const base = `/quest/${questId}/location/${uuid}`;

  const buildPrompt = async (node) => {
    setErrors(null); setBusy(true);
    try {
      const r = await papi(`${base}/prompt`, { node });
      setModal({ node, system: r.system, user: r.user });
    } catch (e) { setErrors([e.message]); } finally { setBusy(false); }
  };

  const strip = {
    border: '1px solid var(--border,#ddd)', borderRadius: 6, padding: '6px 8px', marginBottom: 8,
    background: 'rgba(26,115,232,0.05)', fontSize: 13,
  };
  const linkBtn = { background: 'none', border: 'none', color: '#1a73e8', cursor: 'pointer', padding: 0, fontSize: 12, textDecoration: 'underline' };

  return (
    <div style={strip}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {/* The one glanceable per-point indicator: which pipeline stage this
            point sits at, coloured by state. See the legend above the list. */}
        <StageBadge point={point} />
        <span style={{ fontWeight: 600, color: '#1a73e8' }}>✨ Pipeline</span>
        {/* Derived generation state only. Review/approval is done with the normal
            location tags (Good / Acceptable / ⏳ Waiting for review) below. */}
        <span style={{ background: hasDraft ? '#188038' : '#9aa0a6', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 11, textTransform: 'uppercase' }}>{hasDraft ? 'written' : 'empty'}</span>
        {criticLoop && (() => {
          // Prompt versions the last critic loop ran with (brief · critic ·
          // formalizer) — full SYSTEM texts are inside the dialog.
          const pv = (p) => (p && (p.version || (p.hash || '').slice(0, 7))) || '?';
          return (
            <span
              style={{ fontSize: 11, opacity: 0.7 }}
              title="Prompt versions of the last loop: brief (writer) · critic · formalizer"
            >
              ✍️ v{pv(criticLoop.writer_prompt)} · 🧐 v{pv(criticLoop.critic_prompt)}
              {criticLoop.formal_prompt ? ` · 🗂 v${pv(criticLoop.formal_prompt)}` : ''}
            </span>
          );
        })()}
        <button
          onClick={() => setDialogOpen(true)}
          title="The point's dialog: task candidates → your pick → writer ↔ critic → formalized fields"
          style={{ background: '#1a73e8', color: '#fff', border: 'none', padding: '3px 10px', borderRadius: 4, cursor: 'pointer' }}
        >
          💬 Dialog{criticLoop && criticLoop.rounds_done ? ` (${criticLoop.rounds_done}/${criticLoop.max_rounds})` : ''}
        </button>
        <button disabled={busy} onClick={() => buildPrompt('2a')} style={linkBtn}>Copy prompt</button>
        <button
          onClick={() => setShowGuidance((v) => !v)}
          title="Editorial guidance for this point — fed into the writer's brief and every critic call (empty = the prompts don't change)"
          style={{ ...linkBtn, color: guidanceSaved ? '#188038' : '#1a73e8' }}
        >
          🧭 Guidance{guidanceSaved ? ' •' : ''}
        </button>
      </div>

      {showGuidance && (
        <div style={{ marginTop: 6 }}>
          <textarea
            value={guidanceDraft}
            onChange={(e) => setGuidanceDraft(e.target.value)}
            rows={3}
            placeholder="Editorial guidance for this point (its role in the route's arc). The writer's brief and the critic receive it verbatim; leave empty for no guidance."
            style={{ width: '100%', boxSizing: 'border-box', fontSize: 12, fontFamily: 'inherit' }}
          />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              disabled={guidanceStatus === 'saving' || guidanceDraft.trim() === guidanceSaved}
              onClick={saveGuidance}
              style={{ background: '#188038', color: '#fff', border: 'none', padding: '2px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
            >
              {guidanceStatus === 'saving' ? 'Saving…' : 'Save guidance'}
            </button>
            {guidanceStatus === 'saved' && <span style={{ color: '#188038', fontSize: 12 }}>saved</span>}
            {guidanceStatus === 'error' && <span style={{ color: '#c5221f', fontSize: 12 }}>save failed</span>}
            <span style={{ fontSize: 11, opacity: 0.6 }}>Quest-wide guidance lives in Quest Info; both are combined in the prompts.</span>
          </div>
        </div>
      )}

      {errors && <ul style={{ color: '#c5221f', fontSize: 12, marginTop: 4 }}>{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}

      {modal && (
        <PromptModal node={modal.node} system={modal.system} user={modal.user} onClose={() => setModal(null)} />
      )}

      {dialogOpen && (
        <PointDialogModal
          questId={questId}
          uuid={uuid}
          pointName={location.name}
          onClose={() => setDialogOpen(false)}
          onChanged={onChange}
        />
      )}
    </div>
  );
};

export default PipelineLocationControls;
